/**
 * Format helpers for MIPS dashboard rendering.
 *
 * Per handoff §2 + §5: the backend serializes Decimal values as JSON strings
 * (e.g. "3.00" or "3" with trailing zeros stripped, "99.13" for percent).
 * Components should render those strings directly when possible. The one
 * exception is the Tensaw `<KpiCard>` whose `value` prop is typed `number` —
 * we convert at the call site via `toKpiNumber` and accept the precision
 * tradeoff for display.
 *
 * Everything in this module is a pure function. Unit-tested in
 * `format.test.ts`.
 */

/**
 * Convert a decimal-as-string from the API to a number for the KpiCard prop.
 *
 * - `null` / `undefined` → `null` (KpiCard renders "—")
 * - empty string → `null`
 * - any non-parseable string → `null` (we don't throw; the card just shows
 *   "—" which is the right behavior for a missing/malformed score)
 * - otherwise the parsed number
 *
 * Do not use this for grid cells or text rendering — render the raw string
 * directly per the handoff guidance.
 */
export function toKpiNumber(s: string | null | undefined): number | null {
  if (s === null || s === undefined || s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Render a percent value from the API as `99.1%` (one decimal place).
 *
 * The API ships percents as 0-100 strings ("99.13"). We display one decimal
 * place — matches the mockup and CMS convention. Returns "—" for null /
 * empty / unparseable input.
 */
export function formatPercent(s: string | null | undefined): string {
  const n = toKpiNumber(s);
  if (n === null) return "—";
  return `${n.toFixed(1)}%`;
}

/**
 * Render a points value from the API ("3", "3.5", "12.86") as-is, dropping
 * trailing ".0" if present. Returns "—" for null / empty.
 *
 * We deliberately don't reformat — the backend already strips trailing zeros
 * via Pydantic Decimal serialization, so "3.00" arrives as "3".
 */
export function formatPoints(s: string | null | undefined): string {
  if (s === null || s === undefined || s === "") return "—";
  return s;
}

/**
 * Render an ISO date string ("2026-05-24") as "May 24, 2026" in the user's
 * locale. Returns "—" for null / empty / unparseable input.
 *
 * Uses the platform's `en-US` locale by default since the AMs are US-based.
 */
export function formatDate(s: string | null | undefined): string {
  if (s === null || s === undefined || s === "") return "—";
  // Parse as a calendar date, not a wall-clock moment in the local tz.
  // "2026-05-24" + "T00:00:00" without a timezone is interpreted as local;
  // we want the same calendar day everywhere, so we parse manually.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) {
    // Maybe a full ISO datetime — fall through to Date parsing.
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  const [, y, mo, day] = m;
  // Construct in local tz to display.
  const d = new Date(Number(y), Number(mo) - 1, Number(day));
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Render an ISO datetime ("2026-05-24T03:00:00") as "May 24, 2026, 3:00 AM"
 * in the user's locale. Returns "—" for null / empty / unparseable input.
 *
 * Note: the backend ships datetimes without timezone info; we treat them as
 * UTC (matching the QPP audit convention) and let the browser convert.
 */
export function formatDateTime(s: string | null | undefined): string {
  if (s === null || s === undefined || s === "") return "—";
  // If the string doesn't end in Z and has no offset, append Z so the
  // browser treats it as UTC instead of local.
  const asUtc = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`;
  const d = new Date(asUtc);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Compute a duration string between two ISO datetime strings.
 *
 * Returns null if either input is missing or unparseable; otherwise a string
 * like "44 min" or "1h 12m". Used in the meeting summary line on the
 * dashboard page banner.
 */
export function formatDuration(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): string | null {
  if (!startIso || !endIso) return null;
  const start = new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(startIso) ? startIso : `${startIso}Z`);
  const end = new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(endIso) ? endIso : `${endIso}Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const ms = end.getTime() - start.getTime();
  if (ms <= 0) return null;
  const totalMin = Math.round(ms / 60_000);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/**
 * Map a decile integer from the API (1..10, or `null` for no-benchmark
 * measures) to the StatusBadge taxonomy key registered in
 * `src/lib/taxonomies.ts`.
 *
 * Per handoff ES-9: a measure with a benchmark that falls below D1's lower
 * bound is reported as decile 1, never null. So `null` here means a measure
 * without a benchmark at all — we render `na`.
 */
export function decileToKey(decile: number | null | undefined): string {
  if (decile === null || decile === undefined) return "na";
  if (decile < 1 || decile > 10) return "na";
  return `d${Math.floor(decile)}`;
}
